import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  errorMessage,
  fetchInvestBotChart,
  fetchInvestCandles,
  fetchInvestQuotes,
  previewInvestBot,
  type InvestBot,
  type InvestBotLevel,
  type InvestBotMove,
  type InvestBotPreview,
  type InvestCandle,
  type InvestOverviewItem,
  type InvestQuote,
} from './api';

const compactPrice = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2, notation: 'compact' });
const signedPercent = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always' });
const dayLabel = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'short' });
const timeLabel = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' });

export type InvestTickDirection = 'up' | 'down' | null;

export interface InvestLiveTick extends InvestQuote {
  direction: InvestTickDirection;
  pulse: boolean;
  transport: 'poll' | 'websocket';
}

export type InvestLiveTicks = Record<string, InvestLiveTick>;

function priceLabel(source: string, symbol: string, value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) return 'n/d';
  if (source === 'binance' && symbol.endsWith('EUR')) {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: value < 1 ? 4 : 2,
      maximumFractionDigits: value < 1 ? 6 : 2,
    }).format(value);
  }
  return new Intl.NumberFormat('it-IT', {
    minimumFractionDigits: value < 10 ? 4 : 2,
    maximumFractionDigits: value < 10 ? 6 : 4,
  }).format(value);
}

export function useInvestLiveQuotes(instruments: InvestOverviewItem[], enabled = true) {
  const [ticks, setTicks] = useState<InvestLiveTicks>({});
  const pulseTimers = useRef(new Map<string, number>());
  const quotes = useQuery({
    queryKey: ['invest-quotes'],
    queryFn: fetchInvestQuotes,
    enabled: enabled && instruments.length > 0,
    refetchInterval: 60_000,
    staleTime: 40_000,
    retry: 1,
  });

  const update = useCallback((quote: InvestQuote, transport: InvestLiveTick['transport']) => {
    setTicks((current) => {
      const previous = current[quote.instrumentId];
      if (transport === 'poll' && previous?.transport === 'websocket') {
        const previousTime = Date.parse(previous.asOf);
        const incomingTime = Date.parse(quote.asOf);
        if (!Number.isFinite(incomingTime) || incomingTime <= previousTime) return current;
      }
      const direction = previous && quote.price !== previous.price ? quote.price > previous.price ? 'up' : 'down' : previous?.direction ?? null;
      const pulse = Boolean(previous && quote.price !== previous.price);
      return { ...current, [quote.instrumentId]: { ...quote, direction, pulse, transport } };
    });
    const previousTimer = pulseTimers.current.get(quote.instrumentId);
    if (previousTimer) window.clearTimeout(previousTimer);
    const timer = window.setTimeout(() => {
      setTicks((current) => current[quote.instrumentId]
        ? { ...current, [quote.instrumentId]: { ...current[quote.instrumentId], pulse: false } }
        : current);
      pulseTimers.current.delete(quote.instrumentId);
    }, 720);
    pulseTimers.current.set(quote.instrumentId, timer);
  }, []);

  useEffect(() => {
    quotes.data?.quotes.forEach((quote) => update(quote, 'poll'));
  }, [quotes.data, update]);

  const cryptoKey = instruments
    .filter((item) => item.source === 'binance')
    .map((item) => `${item.id}:${item.symbol}`)
    .sort()
    .join('|');

  useEffect(() => {
    if (!enabled || !cryptoKey || typeof WebSocket === 'undefined') return undefined;
    const sockets = cryptoKey.split('|').map((entry) => {
      const [instrumentId, symbol] = entry.split(':');
      const socket = new WebSocket(`wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@miniTicker`);
      socket.onmessage = (event) => {
        try {
          const payload = JSON.parse(String(event.data)) as { c?: string; E?: number };
          const price = Number(payload.c);
          if (!Number.isFinite(price) || price <= 0) return;
          update({ instrumentId, symbol, source: 'binance', price, asOf: payload.E ? new Date(payload.E).toISOString() : new Date().toISOString() }, 'websocket');
        } catch {
          // Il polling resta attivo come fallback.
        }
      };
      socket.onerror = () => socket.close();
      return socket;
    });
    return () => sockets.forEach((socket) => socket.close());
  }, [cryptoKey, enabled, update]);

  useEffect(() => () => {
    pulseTimers.current.forEach((timer) => window.clearTimeout(timer));
    pulseTimers.current.clear();
  }, []);

  return { ticks, quotes };
}

export function LivePrice({ item, tick, compact = false }: { item: InvestOverviewItem; tick?: InvestLiveTick; compact?: boolean }) {
  const value = tick?.price ?? item.lastClose;
  return <span className={`invest-live-price${tick?.pulse ? ` is-pulse-${tick.direction ?? 'up'}` : ''}`}>
    <strong>{compact && value != null ? compactPrice.format(value) : priceLabel(item.source, item.symbol, value)}</strong>
    {tick && <small><i className={tick.transport === 'websocket' ? 'is-live' : ''}/>{tick.transport === 'websocket' ? 'Live' : `Ore ${timeLabel.format(new Date(tick.asOf))}`}</small>}
  </span>;
}

export function LiveMarketStrip({ instruments, ticks }: { instruments: InvestOverviewItem[]; ticks: InvestLiveTicks }) {
  const visible = instruments.filter((item) => ticks[item.id] || item.lastClose != null).slice(0, 4);
  if (visible.length === 0) return null;
  return <section className="invest-live-strip" aria-label="Prezzi di mercato aggiornati">
    <header><span><i/>Mercato vivo</span><small>Aggiornamento automatico</small></header>
    <div>{visible.map((item) => <span key={item.id}><small>{item.symbol}</small><LivePrice item={item} tick={ticks[item.id]} compact/></span>)}</div>
  </section>;
}

export function BotLiveQuote({ bot, instruments, ticks }: { bot: InvestBot; instruments: InvestOverviewItem[]; ticks: InvestLiveTicks }) {
  const item = (bot.symbols?.length ? instruments.find((row) => bot.symbols?.includes(row.symbol)) : instruments[0]) ?? null;
  if (!item) return null;
  return <span className="invest-bot-live-quote"><small>{item.symbol}</small><LivePrice item={item} tick={ticks[item.id]} compact/></span>;
}

function levelClass(kind: string) {
  if (kind === 'compra') return 'is-buy';
  if (kind === 'vendi') return 'is-sell';
  if (kind === 'media') return 'is-average';
  if (kind === 'stop') return 'is-stop';
  return 'is-filter';
}

function sideClass(side: string) {
  return side.toLowerCase() === 'buy' || side.toLowerCase() === 'cover' ? 'is-buy' : 'is-sell';
}

function safeDay(value: string) {
  return value.slice(0, 10);
}

function BotMarketChart({ item, candles, levels, moves, tick, label }: {
  item: InvestOverviewItem;
  candles: InvestCandle[];
  levels: InvestBotLevel[];
  moves: InvestBotMove[];
  tick?: InvestLiveTick;
  label: string;
}) {
  const [selectedMove, setSelectedMove] = useState<InvestBotMove | null>(null);
  const chart = useMemo(() => {
    const rows = [...candles]
      .filter((row) => Number.isFinite(row.close))
      .sort((a, b) => a.day.localeCompare(b.day))
      .slice(-90)
      .map((row) => ({ ...row }));
    if (tick && rows.length > 0) {
      const last = rows.at(-1)!;
      last.close = tick.price;
      last.high = Math.max(last.high ?? tick.price, tick.price);
      last.low = Math.min(last.low ?? tick.price, tick.price);
    }
    const values = [
      ...rows.flatMap((row) => [row.low ?? row.close, row.high ?? row.close]),
      ...levels.map((level) => level.price),
      ...moves.map((move) => move.price),
    ].filter(Number.isFinite);
    if (rows.length < 2 || values.length === 0) return null;
    const rawMin = Math.min(...values);
    const rawMax = Math.max(...values);
    const padding = Math.max((rawMax - rawMin) * .08, Math.abs(rawMax) * .003, .0001);
    const min = rawMin - padding;
    const max = rawMax + padding;
    const left = 10, right = 278, top = 13, bottom = 188;
    const x = (index: number) => left + index / Math.max(rows.length - 1, 1) * (right - left);
    const y = (value: number) => bottom - (value - min) / Math.max(max - min, .000001) * (bottom - top);
    const indexByDay = new Map(rows.map((row, index) => [safeDay(row.day), index]));
    const movePoints = moves.map((move) => {
      let index = indexByDay.get(safeDay(move.day));
      if (index == null) {
        index = rows.reduce((best, row, rowIndex) => Math.abs(new Date(row.day).getTime() - new Date(move.day).getTime()) < Math.abs(new Date(rows[best].day).getTime() - new Date(move.day).getTime()) ? rowIndex : best, 0);
      }
      return { move, x: x(index), y: y(move.price) };
    });
    const bodyWidth = Math.max(1.4, Math.min(4, (right - left) / rows.length * .64));
    return {
      rows,
      bodyWidth,
      movePoints,
      candles: rows.map((row, index) => ({
        row,
        x: x(index),
        openY: y(row.open ?? row.close),
        closeY: y(row.close),
        highY: y(row.high ?? row.close),
        lowY: y(row.low ?? row.close),
      })),
      levels: levels.map((level) => ({ level, y: y(level.price) })),
      latest: { x: x(rows.length - 1), y: y(rows.at(-1)!.close), value: rows.at(-1)!.close },
    };
  }, [candles, levels, moves, tick]);

  if (!chart) return <div className="invest-live-chart-empty"><strong>Storico insufficiente</strong><p>Servono almeno due rilevazioni per disegnare il bot.</p></div>;
  const onMoveKeyDown = (event: KeyboardEvent<HTMLButtonElement>, move: InvestBotMove) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setSelectedMove(move);
    }
  };

  return <figure className="invest-bot-market-chart">
    <div className="invest-bot-chart-canvas">
      <svg viewBox="0 0 360 204" role="img" aria-label={label}>
        {[48, 95, 142, 188].map((y) => <line key={y} x1="10" x2="278" y1={y} y2={y} className="invest-bot-chart-grid"/>)}
        {chart.candles.map(({ row, x, openY, closeY, highY, lowY }, index) => <g key={`${row.day}-${index}`} className={row.close >= (row.open ?? row.close) ? 'invest-candle is-up' : 'invest-candle is-down'}><line x1={x} x2={x} y1={highY} y2={lowY}/><rect x={x - chart.bodyWidth / 2} y={Math.min(openY, closeY)} width={chart.bodyWidth} height={Math.max(1.5, Math.abs(closeY - openY))}/></g>)}
        {chart.levels.map(({ level, y }, index) => <g className={`invest-bot-level ${levelClass(level.kind)}`} key={`${level.kind}-${level.price}-${index}`}><line x1="10" x2="278" y1={y} y2={y}/><line x1="278" x2="288" y1={y} y2={y}/><text x="292" y={Math.max(11, Math.min(199, y + 3))}>{level.label.length > 11 ? `${level.label.slice(0, 10)}…` : level.label}</text></g>)}
        <line x1="10" x2="286" y1={chart.latest.y} y2={chart.latest.y} className="invest-live-price-line"/>
        <circle cx={chart.latest.x} cy={chart.latest.y} r="4" className={`invest-live-price-dot${tick?.pulse ? ` is-${tick.direction ?? 'up'}` : ''}`}/>
      </svg>
      {chart.movePoints.map(({ move, x, y }, index) => <button type="button" key={`${move.day}-${move.side}-${index}`} className={`invest-bot-move ${sideClass(move.side)}`} style={{ left: `${x / 3.6}%`, top: `${y / 2.04}%` }} aria-label={`${move.side.toLowerCase() === 'buy' ? 'Acquisto' : 'Vendita'} ${dayLabel.format(new Date(`${safeDay(move.day)}T12:00:00`))}: ${move.reason}`} aria-pressed={selectedMove === move} onClick={() => setSelectedMove((current) => current === move ? null : move)} onKeyDown={(event) => onMoveKeyDown(event, move)}><i/></button>)}
    </div>
    <figcaption><span><small>Ultimo prezzo</small><strong>{priceLabel(item.source, item.symbol, chart.latest.value)}</strong></span><span><small>Periodo</small><strong>{dayLabel.format(new Date(`${safeDay(chart.rows[0].day)}T12:00:00`))} · {dayLabel.format(new Date(`${safeDay(chart.rows.at(-1)!.day)}T12:00:00`))}</strong></span></figcaption>
    {selectedMove && <p className={`invest-bot-move-detail ${sideClass(selectedMove.side)}`} role="status"><span>{selectedMove.side.toLowerCase() === 'buy' ? 'Compra' : 'Vendita'}</span><strong>{dayLabel.format(new Date(`${safeDay(selectedMove.day)}T12:00:00`))} · {priceLabel(item.source, item.symbol, selectedMove.price)}</strong><small>{selectedMove.reason}</small></p>}
    <div className="invest-bot-chart-legend" aria-label="Legenda grafico"><span className="is-buy">Compra</span><span className="is-sell">Vendi</span><span className="is-stop">Stop</span><span className="is-average">Media</span></div>
  </figure>;
}

function availableForBot(bot: InvestBot, instruments: InvestOverviewItem[]) {
  const scoped = bot.symbols?.length ? instruments.filter((item) => bot.symbols?.includes(item.symbol)) : instruments;
  return scoped.length > 0 ? scoped : instruments;
}

export function BotLiveChart({ bot, instruments, ticks }: { bot: InvestBot; instruments: InvestOverviewItem[]; ticks: InvestLiveTicks }) {
  const available = useMemo(() => availableForBot(bot, instruments), [bot, instruments]);
  const [instrumentId, setInstrumentId] = useState(available[0]?.id ?? '');
  useEffect(() => {
    if (!available.some((item) => item.id === instrumentId)) setInstrumentId(available[0]?.id ?? '');
  }, [available, instrumentId]);
  const item = available.find((row) => row.id === instrumentId) ?? available[0];
  const candles = useQuery({ queryKey: ['invest-candles', instrumentId, 90], queryFn: () => fetchInvestCandles(instrumentId, 90), enabled: Boolean(instrumentId) });
  const chart = useQuery({ queryKey: ['invest-bot-chart', bot.id, instrumentId, 90], queryFn: () => fetchInvestBotChart(bot.id, instrumentId, 90), enabled: Boolean(instrumentId), staleTime: 30_000 });
  if (!item) return <section className="invest-bot-live-panel"><div className="invest-live-chart-empty"><strong>Nessun mercato compatibile</strong><p>Aggiungi almeno uno strumento dalla stanza Mercati.</p></div></section>;
  const tick = ticks[item.id];
  return <section className="invest-bot-live-panel" aria-labelledby="invest-bot-live-title">
    <header><div><p className="eyebrow">Bot sul mercato</p><h2 id="invest-bot-live-title">Guarda dove sta lavorando</h2><p>Le linee sono le sue regole. I punti sono decisioni realmente registrate.</p></div><span className={tick?.transport === 'websocket' ? 'is-connected' : ''}><i/>{tick?.transport === 'websocket' ? 'Live' : 'Aggiornato'}</span></header>
    {available.length > 1 && <label className="invest-bot-instrument-select"><span>Mercato mostrato</span><select value={item.id} onChange={(event) => setInstrumentId(event.target.value)}>{available.map((row) => <option key={row.id} value={row.id}>{row.label} · {row.symbol}</option>)}</select></label>}
    {(candles.isPending || chart.isPending) && <div className="invest-live-chart-loading" role="status"><i/><i/><i/><span>Disegno prezzi e regole…</span></div>}
    {(candles.isError || chart.isError) && <div className="notice notice-error"><p>{errorMessage(candles.error ?? chart.error)}</p><button type="button" className="btn-secondary" onClick={() => { candles.refetch(); chart.refetch(); }}>Riprova</button></div>}
    {candles.data && chart.data && <BotMarketChart item={item} candles={candles.data} levels={chart.data.livelli} moves={chart.data.mosse} tick={tick} label={`Prezzo di ${item.label} con livelli e decisioni del bot ${bot.name}`}/>} 
    <footer><span aria-hidden="true">i</span><p>Il prezzo si aggiorna automaticamente. Le linee restano ferme finché non cambiano le regole del bot.</p></footer>
  </section>;
}

function paramsSymbols(params: string | Record<string, unknown>) {
  try {
    const parsed = typeof params === 'string' ? JSON.parse(params) as Record<string, unknown> : params;
    return Array.isArray(parsed.symbols) ? parsed.symbols.filter((value): value is string => typeof value === 'string') : [];
  } catch {
    return [];
  }
}

export function BotPreviewPanel({ name, params, instruments, pending, onConfirm, onCancel }: {
  name: string;
  params: string | Record<string, unknown>;
  instruments: InvestOverviewItem[];
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const scopedSymbols = useMemo(() => paramsSymbols(params), [params]);
  const available = useMemo(() => {
    const scoped = scopedSymbols.length > 0
      ? scopedSymbols.map((symbol) => instruments.find((item) => item.symbol === symbol)).filter((item): item is InvestOverviewItem => Boolean(item))
      : instruments;
    return scoped.length > 0 ? scoped : instruments;
  }, [instruments, scopedSymbols]);
  const preferred = scopedSymbols.length > 0 ? available[0] : available.find((item) => item.symbol === 'BTCEUR') ?? available[0];
  const [instrumentId, setInstrumentId] = useState(preferred?.id ?? '');
  useEffect(() => {
    if (!available.some((item) => item.id === instrumentId)) setInstrumentId(preferred?.id ?? '');
  }, [available, instrumentId, preferred?.id]);
  const item = available.find((row) => row.id === instrumentId) ?? preferred;
  const serializedParams = useMemo(() => typeof params === 'string' ? params : JSON.stringify(params), [params]);
  const preview = useQuery<InvestBotPreview>({ queryKey: ['invest-bot-preview', instrumentId, serializedParams, 90], queryFn: () => previewInvestBot(instrumentId, serializedParams, 90), enabled: Boolean(instrumentId), staleTime: 5 * 60_000 });
  const candles = useQuery({ queryKey: ['invest-candles', instrumentId, 90], queryFn: () => fetchInvestCandles(instrumentId, 90), enabled: Boolean(instrumentId) });
  if (!item) return <section className="invest-bot-preview"><header><div><p className="eyebrow">Prima di metterlo in squadra</p><h3>Manca un mercato da provare</h3></div></header><p>Aggiungi uno strumento dalla stanza Mercati, poi torna qui.</p><button type="button" className="btn-quiet" onClick={onCancel}>Chiudi</button></section>;
  return <section className="invest-bot-preview" aria-labelledby="invest-bot-preview-title">
    <header><div><p className="eyebrow">Prima di metterlo in squadra</p><h3 id="invest-bot-preview-title">Prova {name} sul grafico</h3><p>È una simulazione: non muove denaro e non attiva il bot.</p></div><span>90 giorni visibili</span></header>
    <label className="invest-bot-instrument-select"><span>Mercato della prova</span><select value={item.id} onChange={(event) => setInstrumentId(event.target.value)}>{available.map((row) => <option key={row.id} value={row.id}>{row.label} · {row.symbol}</option>)}</select></label>
    {(preview.isPending || candles.isPending) && <div className="invest-live-chart-loading" role="status"><i/><i/><i/><span>Simulo le mosse sul passato…</span></div>}
    {(preview.isError || candles.isError) && <div className="notice notice-error"><p>{errorMessage(preview.error ?? candles.error)}</p><button type="button" className="btn-secondary" onClick={() => { preview.refetch(); candles.refetch(); }}>Riprova</button></div>}
    {preview.data && candles.data && <><BotMarketChart item={item} candles={candles.data} levels={preview.data.livelli} moves={preview.data.mosse} label={`Anteprima di ${name} su ${item.label}`}/><p className="invest-bot-preview-result"><strong>In 400 giorni avrebbe fatto <span className={preview.data.returnPct >= 0 ? 'is-positive' : 'is-negative'}>{signedPercent.format(preview.data.returnPct)}%</span> (il mercato {signedPercent.format(preview.data.buyHoldReturnPct)}%) con {preview.data.trades} operazioni.</strong></p><p className="invest-bot-preview-note"><span aria-hidden="true">i</span>{preview.data.nota}</p></>}
    <div className="invest-bot-preview-actions"><button type="button" className="btn-quiet" disabled={pending} onClick={onCancel}>Annulla</button><button type="button" className="btn-primary" disabled={pending || preview.isPending || !preview.data} onClick={onConfirm}>{pending ? 'Lo preparo…' : 'Conferma e metti in squadra'}</button></div>
  </section>;
}
